import React, { useState, useEffect, useRef, useContext } from "react";
import { InputSelector } from "../../UI/InputSelector";
import { DropdownSelector } from "../../UI/DropdownSelector";
import { getTypeByKey } from "../../../utils/forms/getTypeByKey";
import { fetchFilterOptions } from "../../../utils/api/fetchLeadFilter";
import { updateLeadField } from "../../../utils/api/Lead/updateLead";
import { getAdmins } from "../../../utils/api/Admin/getAdminsApi";
import { getAllDesksApi } from "../../../utils/api/Admin/desk/getAllDesksApi";
import { getAffiliate } from "../../../utils/api/Affiliate/getAffiliateApi";
import styles from "../../../styles/components/Modal/LeadSettings/LeadSettings.module.css";
import { LangContext } from "../../../context/LangContext";
import { ThemeContext } from "../../../context/ThemeContext";
import { mapGeoOptions } from "../../../utils/geoCountryNames";

const translations = {
    ru: {
        title: "Изменить лида",
        confirmButton: "Подтвердить",
        errorLoadingData: "Не удалось загрузить данные.",
        errorLoadingManagers: "Не удалось загрузить список менеджеров.",
        errorLoadingDesks: "Не удалось загрузить список десков.",
        errorLoadingAffiliates: "Не удалось загрузить список аффилиатов.",
        errorUpdatingLead: "Произошла ошибка при обновлении лида.",
        errorBotProfitPercentRange: "Профит % ботов должен быть в диапазоне от -100 до 100.",
    },
    en: {
        title: "Edit Lead",
        confirmButton: "Confirm",
        errorLoadingData: "Failed to load data.",
        errorLoadingManagers: "Failed to load manager list.",
        errorLoadingDesks: "Failed to load desk list.",
        errorLoadingAffiliates: "Failed to load affiliate list.",
        errorUpdatingLead: "An error occurred while updating the lead.",
        errorBotProfitPercentRange: "Bot profit % must be between -100 and 100.",
    },
};


export const LeadSettings = ({ setIsOpenModal, modalData, onSave, role }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const currentTranslation = translations[langSelected] || translations.ru;
    const isDark = theme === 'dark';
    const hasModalValue = modalData.value !== null && modalData.value !== undefined;

    const [inputValue, setInputValue] = useState(hasModalValue ? modalData.value.toString() : "");
    const [dropdownOptions, setDropdownOptions] = useState([]);
    const [error, setError] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const modalRef = useRef(null);

    const fieldConfig = getTypeByKey(modalData.key);

    useEffect(() => {
        if (fieldConfig?.type === "dropdown" && dropdownOptions.length > 0 && hasModalValue) {
            const modalValueAsString = modalData.value.toString();
            const initialOption = dropdownOptions.find(
                (option) => option.value?.toString() === modalValueAsString || option.label === modalData.value
            );
            if (initialOption) {
                setInputValue(initialOption.value);
            } else {
                setInputValue("");
            }
        } else if (fieldConfig?.type === "input" && hasModalValue) {
            setInputValue(modalData.value.toString());
        }
    }, [modalData, fieldConfig, dropdownOptions, hasModalValue]);

    useEffect(() => {
        const loadDropdownData = async () => {
            if (fieldConfig?.type === "dropdown") {
                let options = [];
                if (fieldConfig?.endpoint) {
                    try {
                        options = await fetchFilterOptions(fieldConfig.endpoint, langSelected);
                    } catch (error) {
                        setError(currentTranslation.errorLoadingData);
                        return;
                    }
                } else if (fieldConfig?.key === "manager_id") {
                    try {
                        const adminsData = await getAdmins();
                        const formattedManagers = adminsData.data.map(admin => ({ value: admin.id, label: (admin.first_name ? `${admin.first_name} ${admin.last_name || ''}`.trim() : admin.email) || "", }));
                        setDropdownOptions(formattedManagers);
                        if (hasModalValue) {
                            const modalValueAsString = modalData.value.toString();
                            const initialOption = formattedManagers.find(
                                (option) => option.value?.toString() === modalValueAsString
                            );
                            if (initialOption) {
                                setInputValue(initialOption.value);
                            } else {
                                setInputValue("");
                            }
                        }
                        return;
                    } catch (error) {
                        setError(currentTranslation.errorLoadingManagers);
                        return;
                    }
                } else if (fieldConfig?.key === "desk_id") {
                    try {
                        const desksData = await getAllDesksApi();
                        const formattedDesks = desksData.data.map(desk => ({ value: desk.id, label: desk.name }));
                        setDropdownOptions(formattedDesks);
                        if (hasModalValue) {
                            const modalValueAsString = modalData.value.toString();
                            const initialOption = formattedDesks.find(
                                (option) => option.value?.toString() === modalValueAsString
                            );
                            if (initialOption) {
                                setInputValue(initialOption.value);
                            } else {
                                setInputValue("");
                            }
                        }
                        return;
                    } catch (error) {
                        setError(currentTranslation.errorLoadingDesks);
                        return;
                    }
                } else if (fieldConfig?.key === "affiliator_id") {
                    try {
                        const affiliatesData = await getAffiliate();
                        console.log(affiliatesData);
                        const formattedAffiliates = affiliatesData.data.map(affiliate => ({ value: affiliate.id, label: (affiliate.first_name ? `${affiliate.first_name} ${affiliate.last_name || ''}`.trim() : affiliate.affiliate) || affiliate.email || "",}));
                        console.log(formattedAffiliates);
                        setDropdownOptions(formattedAffiliates);
                        if (hasModalValue) {
                            const modalValueAsString = modalData.value.toString();
                            const initialOption = formattedAffiliates.find(
                                (option) => option.value?.toString() === modalValueAsString
                            );
                            if (initialOption) {
                                setInputValue(initialOption.value);
                            } else {
                                setInputValue("");
                            }
                        }
                        return;
                    } catch (error) {
                        setError(currentTranslation.errorLoadingAffiliates);
                        return;
                    }
                }

                const formattedOptions = options.map(option => {
                    if (typeof option === 'string') {
                        return { value: option, label: option };
                    } else if (typeof option === 'object' && option !== null) {
                        return {
                            value: option.value !== undefined ? option.value : (option.id !== undefined ? option.id : Object.values(option)[0]),
                            label: option.label !== undefined ? option.label : (option.name !== undefined ? option.name : Object.values(option)[0]?.toString()),
                            iconPath: option.iconPath || null,
                        };
                    }
                    return { value: '', label: '' };
                });

                const normalizedOptions = fieldConfig?.key === 'geo'
                    ? mapGeoOptions(formattedOptions, langSelected)
                    : formattedOptions;

                setDropdownOptions(normalizedOptions);
                if (hasModalValue) {
                    const initialOption = normalizedOptions.find(
                        (option) => option.value?.toString() === modalData.value?.toString()
                    );
                    if (initialOption) {
                        setInputValue(initialOption.value);
                    } else {
                        setInputValue("");
                    }
                }
            } else if (fieldConfig?.type === "input" && hasModalValue) {
                setInputValue(modalData.value.toString());
            }
        };

        loadDropdownData();
    }, [fieldConfig, modalData.value, currentTranslation, hasModalValue, langSelected]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setIsOpenModal(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [setIsOpenModal]);

    if (!fieldConfig) {
        return null;
    }

    const handleChange = (key, value) => {
        setInputValue(value);
        setError("");
    };

    const handleFocus = () => {
        setIsFocused(true);
        setError("");
    };

    const handleBlur = () => {
        setIsFocused(false);
    };

    const handleConfirm = async () => {
        try {
            let valueToSend = inputValue;

            if ((fieldConfig?.formKey === 'manager_id' || fieldConfig?.formKey === 'geo' || fieldConfig?.formKey === 'bot_profit_percent') && inputValue === '') {
                valueToSend = null;
            } else if (fieldConfig?.formKey === 'manager_id' || fieldConfig?.formKey === 'affiliator_id' || fieldConfig?.formKey === 'desk_id') {
                valueToSend = parseInt(inputValue, 10);
            } else if (fieldConfig?.formKey === 'bot_profit_percent') {
                valueToSend = parseFloat(inputValue);

                if (Number.isNaN(valueToSend) || valueToSend < -100 || valueToSend > 100) {
                    setError(currentTranslation.errorBotProfitPercentRange);
                    return;
                }
            }
            await updateLeadField(modalData.id, fieldConfig?.formKey, valueToSend, role);
            if (onSave) {
                onSave();
            }
            setIsOpenModal(false);
        } catch (err) {
            if (err && err.errors && err.errors[0]?.message) {
                setError(err.errors[0]?.message);
            } else if (err && err.errors) {
                setError(err.errors[modalData.key]);
            } else if (err && err.message) {
                setError(err.message);
            } else {
                setError(currentTranslation.errorUpdatingLead);
            }
        }
    };

    return (
        <div className={`${styles.leadSettingsModal} ${isDark ? styles.darkLeadSettingsModal : ''}`}>
            <div className={`${styles.modalContent} ${isDark ? styles.darkModalContent : ''}`} ref={modalRef}>
                <div className={`${styles.modalHeader} ${isDark ? styles.darkModalHeader : ''}`}>
                    <p className={`${styles.modalTitle} ${isDark ? styles.darkModalTitle : ''}`}>{currentTranslation.title}</p>
                </div>

                <div className={styles.formContainer}>
                    <div className={styles.formGroup}>
                        <div className={styles.labelContainer}>
                            <p className={`${styles.label} ${isDark ? styles.darkLabel : ''}`}>{modalData.label}</p>
                        </div>

                        <div className={styles.container}>
                            {fieldConfig?.type === "input" ? (
                                <InputSelector
                                    filterName={modalData.key}
                                    selectedValue={inputValue}
                                    handleFilterChange={handleChange}
                                    hasError={!!error}
                                    errorMessage={error?.[modalData.key] || error}
                                    isFocused={isFocused}
                                    onFocus={handleFocus}
                                    onBlur={handleBlur}
                                />
                            ) : (
                                <DropdownSelector
                                    filterName={modalData.key}
                                    selectedValue={inputValue}
                                    options={dropdownOptions}
                                    handleFilterChange={handleChange}
                                    hasError={!!error}
                                    errorMessage={error}
                                    onFocus={handleFocus}
                                    onBlur={handleBlur}
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div className={styles.buttonContainer}>
                    <button
                        className={`${styles.confirmButton} ${isDark ? styles.darkConfirmButton : ''}`}
                        onClick={handleConfirm}
                    >
                        {currentTranslation.confirmButton}
                    </button>
                </div>
            </div>
        </div>
    );
};
